import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { WalletService } from '../../services/WalletService/wallet';
import { WalletResponse, WalletTransactionResponse } from '../../models/wallet.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';

@Component({
  selector: 'app-wallet',
  imports: [CommonModule, FormsModule, ErrorBanner, EmptyState, PageSizeSelect],
  templateUrl: './wallet.html',
  styleUrl: './wallet.scss',
})
export class Wallet implements OnInit {
  wallet = signal<WalletResponse | null>(null);
  transactions = signal<WalletTransactionResponse[]>([]);

  isLoading = signal(false);
  errorMessage = signal('');

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);
  totalPages = signal(1);
  totalCount = signal(0);

  constructor(private walletService: WalletService) {}

  get rangeStart(): number {
    return this.totalCount() === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount());
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages();
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
    this.loadTransactions();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages() || page === this.page()) {
      return;
    }
    this.page.set(page);
    this.loadTransactions();
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.walletService.getWallet().subscribe({
      next: (res) => {
        this.wallet.set(res.data);
        this.loadTransactions();
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load wallet.'));
      },
    });
  }

  private loadTransactions(): void {
    this.isLoading.set(true);

    this.walletService.listTransactions(this.page(), this.pageSize()).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.transactions.set(res.data);
        this.totalPages.set(res.page?.total_pages ?? 1);
        this.totalCount.set(res.page?.count ?? res.data.length);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load wallet transactions.'));
      },
    });
  }
}
